import { useState } from "react";
import { isValidPhone } from "../utils/phoneNumber";
import { useTranslation } from "./useTranslation";

/**
 * Shared behaviour for every phone field: strip as you type, complain only once you leave.
 *
 * `clean` is applied on every keystroke so what is on screen is exactly what gets stored —
 * the same strip the API performs on write (App\Rules\PhoneNumber::clean). Without it a user
 * types "(202) 555-0123", the server saves "2025550123", and the number silently changes
 * shape between the form and the contact card.
 *
 * The error appears only after BLUR. Validating on every keystroke would mark the field red
 * from the first digit to the seventh on every number anyone ever types, which trains people
 * to ignore it.
 *
 * `submitAttempted` is the exception. A number that came from the database is never focused,
 * so blur alone would leave it unmarked while the save silently keeps failing — the user sees
 * a toast and no red field. Once a submit has been blocked we show the error regardless of
 * blur; it disappears again the moment the value becomes valid.
 */
export const usePhoneValidation = (
  value: string | undefined,
  submitAttempted = false,
) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  const isError = (touched || submitAttempted) && !isValidPhone(value);

  return {
    isError,
    helperText: isError ? t("general.invalid_phone") : undefined,
    onBlur: () => setTouched(true),
    // Typing again clears a standing error immediately rather than making the user blur
    // a second time to find out whether they have fixed it.
    onEdit: () => setTouched(false),
  };
};
